import { useCallback, useEffect, useState } from "react";

const SWIPE_THRESHOLD = 10;
const SWIPE_LEFT = "left";
export const SWIPE_RIGHT = "right";

/**
 * State diagram:
 *
 * DEFAULT   --onStart--> STARTED
 * STARTED   --onMove-->  SWIPING_X (if |dx| > |dy| and |dx| > SWIPE_THRESHOLD)
 * STARTED   --onMove-->  DEFAULT (if |dy| > |dx| and |dy| > SWIPE_THRESHOLD)
 * STARTED   --onEnd-->   DEFAULT
 * SWIPING_X --onEnd-->   DEFAULT
 *
 * Note: we lock the page so it doesn't scroll vertically when a swipe is detected
 */
const States = {
  DEFAULT: 0, // Starting state - nothing happening
  STARTED: 1, // User touched target element
  SWIPING_X: 10, // User is swiping horizontally
};

const getGesturePointFromEvent = (e, lastPoint) => {
  const point = {};

  if (e.changedTouches) {
    point.x = e.changedTouches[0].screenX;
    point.y = e.changedTouches[0].screenX;
  } else {
    point.x = e.clientX;
    point.y = e.clientY;
  }

  if (point.x === 0 && point.y === 0 && lastPoint) {
    point.x = lastPoint.x;
    point.y = lastPoint.y;
  }

  return point;
};

/**
 * This hook monitors touch events in the given element and calls methods in the given listener when different gestures occur
 *
 * Currently only supports swipes left and right. Feel free to add others as needed.
 */
export const useSwipeController = (element, listener = null) => {
  const [swipeState, setSwipeState] = useState(States.DEFAULT);
  const [startPoint, setStartPoint] = useState(null);
  const [lastPoint, setLastPoint] = useState(null);
  const [preventClick, setPreventClick] = useState(false);

  const onMove = useCallback(
    (e) => {
      if (swipeState !== States.STARTED) {
        return;
      }

      const p = getGesturePointFromEvent(e);

      const dx = p.x - startPoint.x;
      const absDx = Math.abs(dx);
      const dy = p.y - startPoint.y;
      const absDy = Math.abs(dy);

      if (absDx >= absDy && absDx > SWIPE_THRESHOLD) {
        setSwipeState(States.SWIPING_X);
        setLastPoint(p);
        e.preventDefault();
        if (listener.onMove) listener.onMove(p);
      } else if (absDy > absDx && absDy > SWIPE_THRESHOLD) {
        setSwipeState(States.DEFAULT);
        setLastPoint(null);
        setStartPoint(null);
      }
    },
    [listener, swipeState, startPoint],
  );

  const onEnd = useCallback(
    (e) => {
      if (swipeState === States.DEFAULT) {
        return;
      }

      if (swipeState === States.SWIPING_X) {
        setPreventClick(true); // swipe overrides any potential click event
        const p = getGesturePointFromEvent(e, lastPoint);
        if (listener.onEnd) listener.onEnd(p);

        const dx = p.x - startPoint.x;
        if (listener.onSwipe) listener.onSwipe(dx < 0 ? SWIPE_LEFT : SWIPE_RIGHT);

        e.preventDefault();
      }

      setSwipeState(States.DEFAULT);
      setStartPoint(null);
      setLastPoint(null);
    },
    [listener, swipeState, startPoint, lastPoint],
  );

  const onClick = useCallback(
    (e) => {
      if (!preventClick) {
        return;
      }

      e.preventDefault();
      setPreventClick(false);
    },
    [preventClick],
  );

  const onStart = useCallback(
    (e) => {
      if (listener === null) {
        return;
      }

      const p = getGesturePointFromEvent(e);
      setStartPoint(p);
      setSwipeState(States.STARTED);
      if (listener.onStart) listener.onStart(p);

      // Note, we do not call preventDefault until we know for sure it's a swipe
    },
    [listener],
  );

  const lockVerticalScrolling = useCallback((locked) => {
    const { style } = document.querySelector("body");
    if (locked && style.overflowX !== "hidden") {
      style.overflowX = "hidden";
    }
    if (!locked && style.overflowX !== "auto") {
      style.overflowX = "auto";
    }
  }, []);

  // Manage scroll lock while swiping
  useEffect(() => {
    lockVerticalScrolling(swipeState === States.SWIPING_X);
  }, [swipeState, lockVerticalScrolling]);

  // Clean up after component is unmounted
  useEffect(() => () => lockVerticalScrolling(false), [lockVerticalScrolling]);

  // Add and remove listeners as needed
  useEffect(() => {
    if (element) {
      element.addEventListener("touchstart", onStart, { capture: true });
      element.addEventListener("touchmove", onMove, { capture: true });
      element.addEventListener("touchend", onEnd, { capture: true });
      element.addEventListener("click", onClick, { capture: true });
    }
    return () => {
      if (!element) {
        return;
      }

      element.removeEventListener("touchstart", onStart, true);
      element.removeEventListener("touchmove", onMove, true);
      element.removeEventListener("touchend", onEnd, true);
      element.removeEventListener("click", onClick, true);
    };
  }, [element, onStart, onMove, onEnd, onClick]);
};
